import {
  type Company,
  type CompanyBranch,
  setIsCompaniesRequested,
  setCompanies,
  type queryKboResponse,
} from '../../../Store/DataSlice'
import uuid4 from 'uuid4'
import './CompanyAdd.scss'
import { useEffect, useMemo, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import {
  getSimplifiedVatNumber,
  isValidEmail,
  isValidNaceCode,
  isValidVatNumber,
  isValidWebsite,
} from '../../../Utils/Validations'
import { CountryCodes, websitePlaceholder } from '../../../Utils/Constants'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { NumberInput } from '../../../Components/NumberInput/NumberInput'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { formatCompanyBranch } from '../../../Utils/Formatting'
import { type RootState } from '../../../Store/Store'
import { getNaceSelectOptions } from '../../../Utils/NaceCodes'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'

let latestFetchId: string | undefined
const accidentMostRecentYear = new Date().getFullYear() - 1
const emptyCompany: Company = {
  vatNumber: '',
  name: '',
  legalForm: '',
  email: '',
  website: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  countryCode: 'BE',
  naceCode: '',
  branches: [],
  accidentCounts: [{ count: 0, year: accidentMostRecentYear }],
  lastKpbCheck: undefined,
  lastKpbUpdate: undefined,
  lastKpbCheckSuccess: undefined,
}
const emptyBranchToAdd: CompanyBranch = {
  addressLine1: '',
  city: '',
  number: '',
  postalCode: '',
  countryCode: 'BE',
}

interface CompanyAddProps {
  onSaveAction?: () => void
}

export function CompanyAdd({ onSaveAction }: CompanyAddProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('company')
  const countryLocalization = useLocalization('country')
  const [allNaceSelectOptions] = useState(getNaceSelectOptions())

  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])
  const allCompanies = useSelector((state: RootState) => state.data.companies)

  const [company, setCompany] = useState<Company>({ ...emptyCompany })

  const [vatNumberIsValid, setVatNumberIsValid] = useState(false)
  const [isFetchDataInProgress, setIsFetchDataInProgress] = useState(false)
  const [branchToAdd, setBranchToAdd] = useState({ ...emptyBranchToAdd })
  const [accidentCountYearToAdd, setAccidentCountYearToAdd] = useState(
    accidentMostRecentYear - 1
  )

  const [kboLookupFailed, setKboLookupFailed] = useState(false)
  const isBelgianVatNumber = useMemo(
    () =>
      !!company.vatNumber &&
      !kboLookupFailed &&
      company.vatNumber.length > 2 &&
      company.vatNumber.substring(0, 2) === 'BE',
    [company, kboLookupFailed]
  )
  const onVatNumberChange = (newVatNumber: string) => {
    const simplifiedVatNumber = getSimplifiedVatNumber(newVatNumber)
    const isValid =
      simplifiedVatNumber?.length > 2 && isValidVatNumber(simplifiedVatNumber)
    setVatNumberIsValid(isValid)
    const companyUpdates: Partial<Company> = {
      vatNumber: simplifiedVatNumber,
    }
    if (isValid) {
      if (allCompanies.some((c) => c.vatNumber === simplifiedVatNumber)) {
        dispatch(
          setDestinationView({
            destinationView: 'company-edit',
            detailKey: simplifiedVatNumber,
          })
        )
        return
      }
      const countryCode = simplifiedVatNumber.substring(0, 2).toUpperCase()
      if (countryCode === 'BE') {
        setIsFetchDataInProgress(true)
        setKboLookupFailed(false)
        const fetchId = uuid4()
        latestFetchId = fetchId

        fetch(
          `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/query-kruispuntbank?vatNumber=${simplifiedVatNumber}`,
          { headers: { ...getAuthorizationHeader() } }
        )
          .then(async (response) => await response.json())
          .then((response: queryKboResponse) => {
            if (fetchId === latestFetchId) {
              if (response.success) {
                setCompany((p) => ({
                  ...p,
                  ...response.lookupData,
                  lastKpbCheck: Date.now(),
                  lastKpbUpdate: Date.now(),
                  lastKpbCheckSuccess: true,
                }))
              } else {
                setKboLookupFailed(true)
                setCompany((p) => ({
                  ...p,
                  lastKpbCheck: Date.now(),
                  lastKpbCheckSuccess: false,
                }))
                console.warn(
                  `KBO lookup failed for ${simplifiedVatNumber}`,
                  response.error
                )
                alert(
                  `KBO lookup failed for ${simplifiedVatNumber}: ${response.error}`
                )
              }
              setIsFetchDataInProgress(false)
            } else {
              console.log(`Dropping outdated fetch result: `, response)
            }
          })
          .catch((err) => {
            console.warn(`fetch query-kruispuntbank failed: `, err)
          })
      } else {
        if (CountryCodes.includes(countryCode)) {
          companyUpdates.countryCode = countryCode
          setBranchToAdd((previous) => ({
            ...previous,
            countryCode,
          }))
        }
      }
    } else {
      latestFetchId = undefined
      setIsFetchDataInProgress(false)
    }
    setCompany((p) => ({ ...p, ...companyUpdates }))
  }

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []
    if (!isBelgianVatNumber) {
      if (!company.name)
        newValidationErrors.push({ property: 'name', type: 'isRequired' })

      if (!company.legalForm)
        newValidationErrors.push({ property: 'legalForm', type: 'isRequired' })

      if (!company.countryCode)
        newValidationErrors.push({
          property: 'countryCode',
          type: 'isRequired',
        })

      if (!company.naceCode)
        newValidationErrors.push({ property: 'naceCode', type: 'isRequired' })
      else if (!isValidNaceCode(company.naceCode))
        newValidationErrors.push({ property: 'naceCode', type: 'format' })
    }
    if (company.website && !isValidWebsite(company.website))
      newValidationErrors.push({ property: 'website', type: 'format' })
    if (company.email && !isValidEmail(company.email))
      newValidationErrors.push({ property: 'email', type: 'format' })
    setValidationErrors(newValidationErrors)
  }, [company, isBelgianVatNumber])

  const [branchValidationErrors, setBranchValidationErrors] = useState<
    validationError[]
  >([])
  useEffect(() => {
    const newBranchValidationErrors: validationError[] = []
    if ((branchToAdd.number?.length ?? 0) === 0)
      newBranchValidationErrors.push({
        property: 'branch-number',
        type: 'isRequired',
      })
    if (!branchToAdd.countryCode)
      newBranchValidationErrors.push({
        property: 'branch-countryCode',
        type: 'isRequired',
      })
    setBranchValidationErrors(newBranchValidationErrors)
  }, [branchToAdd])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCompany = () => {
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          company,
        }),
      }
    )
      .then(() => {
        dispatch(setCompanies([...allCompanies, company]))
        if (onSaveAction) onSaveAction()
        else {
          setTimeout(() => {
            dispatch(setDestinationView({ destinationView: 'company-list' }))
          }, 1)
        }
      })
      .catch((err) => {
        console.warn(`fetch company-add failed: `, err)
      })
  }

  return (
    <div className="company-add">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'company-save-in-progress',
            'De onderneming word momenteel opgeslagen.'
          )}
        </div>
      ) : isFetchDataInProgress ? (
        <div className="vat-fetch-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'vat-info-fetch-in-progress',
            'Gegevens voor onderneming {vatNumber} worden opgehaald',
            { vatNumber: company.vatNumber }
          )}
        </div>
      ) : (
        <>
          <TextInput
            label={localization.text('vat-number', 'Ondernemingsnummer')}
            placeholder="BE0999999999"
            onChange={onVatNumberChange}
            isValid={vatNumberIsValid}
            isReadOnly={saveInProgress}
            value={company.vatNumber}
            helperText={
              !vatNumberIsValid
                ? localization.text(
                    'invalid-vat-number',
                    'Geef een geldig vat nummer startende met de landcode'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('name', 'Naam')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({
                ...previous,
                name: newValue,
              }))
            }}
            value={company.name}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'name')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'name')
                    ?.type === 'isRequired'
                  ? localization.text(
                      'name-missing',
                      'Geef een geldige naam in'
                    )
                  : undefined
                : undefined
            }
          />
          <TextInput
            label={localization.text('legal-form', 'Rechtsvorm')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, legalForm: newValue }))
            }}
            value={company.legalForm}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'legalForm')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'legalForm')
                    ?.type === 'isRequired'
                  ? localization.text(
                      'legalForm-missing',
                      'Geef een rechtsvorm in'
                    )
                  : undefined
                : undefined
            }
          />
          <TextInput
            label={localization.text('email', 'Email')}
            isReadOnly={!vatNumberIsValid || saveInProgress}
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, email: newValue }))
            }}
            value={company.email}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'email')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'email')
                    ?.type === 'format'
                  ? localization.text(
                      'email-invalid',
                      'Email formaat is incorrect'
                    )
                  : undefined
                : undefined
            }
          />
          <TextInput
            label={localization.text('website', 'Website')}
            isReadOnly={!vatNumberIsValid || saveInProgress}
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, website: newValue }))
            }}
            value={company.website}
            placeholder={websitePlaceholder}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'website')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'website')
                    ?.type === 'format'
                  ? localization.text(
                      'website-invalid',
                      'Website formaat is incorrect'
                    )
                  : undefined
                : undefined
            }
          />
          <TextInput
            label={localization.text('addressLine1', 'Straat en huisnummer')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({
                ...previous,
                addressLine1: newValue,
              }))
            }}
            value={company.addressLine1}
          />
          <TextInput
            label={localization.text('postalCode', 'Postcode')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, postalCode: newValue }))
            }}
            value={company.postalCode}
          />
          <TextInput
            label={localization.text('city', 'Gemeente')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, city: newValue }))
            }}
            value={company.city}
          />
          <SingleSelect
            label={localization.text('country', 'Land')}
            items={CountryCodes.map((code) => ({
              value: code,
              label: code ? countryLocalization.text(code, code) : '',
            }))}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCompany((previous) => ({ ...previous, countryCode: newValue }))
            }}
            value={company.countryCode}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'countryCode')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'countryCode')
                    ?.type === 'isRequired'
                  ? localization.text(
                      'country-code-missing',
                      'Kies een geldig land'
                    )
                  : undefined
                : undefined
            }
          />
          <SingleSelect
            label={localization.text('naceCode', 'NACE Code')}
            isReadOnly={saveInProgress}
            items={allNaceSelectOptions}
            onChange={(item) => {
              setCompany((p) => ({
                ...p,
                naceCode: item,
              }))
            }}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'naceCode')
            }
            value={company.naceCode}
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'naceCode')
                    ?.type === 'isRequired'
                  ? localization.text(
                      'nace-number-missing',
                      'Geef een geldige nace code in'
                    )
                  : validationErrors.find((ve) => ve.property === 'naceCode')
                      ?.type === 'format'
                  ? localization.text(
                      'nace-number-invalid',
                      'Nace code formaat is incorrect'
                    )
                  : undefined
                : undefined
            }
          />

          <div className="accident-counts">
            <div className="label">
              {localization.text(
                'accident-counts',
                'Arbeidsongevallen cijfers'
              )}
            </div>
            {vatNumberIsValid ? (
              <>
                {company.accidentCounts.map((ac) => (
                  <div key={ac.year} className="accident-count">
                    <div className="accident-count-year">{ac.year}</div>
                    <NumberInput
                      value={ac.count}
                      min={0}
                      max={99999.99}
                      precision={2}
                      onChange={(newValue) => {
                        setCompany((previous) => ({
                          ...previous,
                          accidentCounts: previous.accidentCounts.map((pac) =>
                            pac.year === ac.year
                              ? { year: ac.year, count: newValue }
                              : pac
                          ),
                        }))
                      }}
                    />
                    <Button
                      importance={'secondary'}
                      text={localization.text(
                        'remove-accident-count',
                        'Verwijder jaar'
                      )}
                      onClick={() => {
                        setCompany((previous) => ({
                          ...previous,
                          accidentCounts: previous.accidentCounts.filter(
                            (b) => b.year !== ac.year
                          ),
                        }))
                      }}
                    />
                  </div>
                ))}
                <div className="add-accident-count">
                  <NumberInput
                    value={accidentCountYearToAdd}
                    onChange={(newValue) => {
                      setAccidentCountYearToAdd(newValue)
                    }}
                    max={accidentMostRecentYear}
                    min={1900}
                  />
                  <Button
                    importance={'secondary'}
                    text={localization.text(
                      'add-accident-count',
                      'Voeg jaar toe'
                    )}
                    onClick={() => {
                      setCompany((previous) => ({
                        ...previous,
                        accidentCounts: [
                          ...previous.accidentCounts,
                          { year: accidentCountYearToAdd, count: 0 },
                        ],
                      }))
                      setAccidentCountYearToAdd((previous) => previous - 1)
                    }}
                    disabled={company.accidentCounts.some(
                      (ac) => ac.year === accidentCountYearToAdd
                    )}
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="branches">
            <div className="label">
              {localization.text('branches', 'Vestigingen')}
            </div>
            {vatNumberIsValid ? (
              isBelgianVatNumber ? (
                company.branches.map((branch) => (
                  <div className="be-branch-option" key={branch.number}>
                    {formatCompanyBranch(branch, countryLocalization)}
                  </div>
                ))
              ) : (
                <>
                  {company.branches.map((branch) => (
                    <div key={branch.number} className="int-branch-option">
                      <div className="branch-name">
                        {formatCompanyBranch(branch, countryLocalization)}
                      </div>
                      <Button
                        importance={'secondary'}
                        text={localization.text(
                          'remove-branch',
                          'Verwijder vestiging'
                        )}
                        size="small"
                        onClick={() => {
                          setCompany((previous) => ({
                            ...previous,
                            branches: previous.branches.filter(
                              (b) => b !== branch
                            ),
                          }))
                        }}
                      />
                    </div>
                  ))}
                  <div className="add-branch">
                    <h3>
                      {localization.text('add-branch', 'Voeg vestiging toe')}
                    </h3>
                    <TextInput
                      label={localization.text(
                        'branch-number',
                        'Vestigingsnummer'
                      )}
                      isReadOnly={saveInProgress}
                      onChange={(newValue) => {
                        setBranchToAdd((p) => ({ ...p, number: newValue }))
                      }}
                      value={branchToAdd.number}
                      helperText={
                        branchValidationErrors.find(
                          (ve) => ve.property === 'branch-number'
                        )?.type === 'isRequired'
                          ? localization.text(
                              'branch-number-is-required',
                              'Vestigingsnummer is verplicht'
                            )
                          : undefined
                      }
                    />
                    <TextInput
                      label={localization.text(
                        'branch-addressLine1',
                        'Straat en huisnummer'
                      )}
                      isReadOnly={saveInProgress}
                      onChange={(newValue) => {
                        setBranchToAdd((p) => ({
                          ...p,
                          addressLine1: newValue,
                        }))
                      }}
                      value={branchToAdd.addressLine1}
                    />
                    <TextInput
                      label={localization.text('branch-postalCode', 'Postcode')}
                      isReadOnly={saveInProgress}
                      onChange={(newValue) => {
                        setBranchToAdd((p) => ({ ...p, postalCode: newValue }))
                      }}
                      value={branchToAdd.postalCode}
                    />
                    <TextInput
                      label={localization.text('branch-city', 'Gemeente')}
                      isReadOnly={saveInProgress}
                      onChange={(newValue) => {
                        setBranchToAdd((p) => ({ ...p, city: newValue }))
                      }}
                      value={branchToAdd.city}
                    />

                    <SingleSelect
                      label={localization.text('branch-country', 'Land')}
                      items={CountryCodes.map((code) => ({
                        value: code,
                        label: code ? countryLocalization.text(code, code) : '',
                      }))}
                      isReadOnly={saveInProgress}
                      onChange={(newValue) => {
                        setBranchToAdd((previous) => ({
                          ...previous,
                          countryCode: newValue,
                        }))
                      }}
                      value={branchToAdd.countryCode}
                      isValid={
                        !branchValidationErrors.some(
                          (ve) => ve.property === 'branch-countryCode'
                        )
                      }
                      helperText={
                        branchValidationErrors.find(
                          (ve) => ve.property === 'branch-countryCode'
                        )?.type === 'isRequired'
                          ? localization.text(
                              'branch-country-code-missing',
                              'Kies een geldig land'
                            )
                          : undefined
                      }
                    />
                    {branchValidationErrors.length === 0 && !saveInProgress ? (
                      <Button
                        importance={'primary'}
                        text={localization.text(
                          'add-branch',
                          'Voeg vestiging toe'
                        )}
                        onClick={() => {
                          setCompany((previous) => ({
                            ...previous,
                            branches: [
                              ...(
                                previous.branches ??
                                company.branches ??
                                []
                              ).filter((b) => b !== branchToAdd),
                              branchToAdd,
                            ],
                          }))
                          setBranchToAdd({
                            ...emptyBranchToAdd,
                            countryCode: branchToAdd.countryCode,
                          })
                        }}
                      />
                    ) : null}
                  </div>
                </>
              )
            ) : null}
          </div>
          {company.lastKpbCheck != null && (
            <div className="display-field">
              <div className="display-label">
                {localization.text('lastKpbCheck', 'Laatste Kbp check')}
              </div>
              <div className="display-value">
                {company.lastKpbCheck != null
                  ? new Date(company.lastKpbCheck).toLocaleDateString()
                  : ''}
              </div>
            </div>
          )}

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-add-button',
                'Voeg onderneming toe'
              )}
              onClick={saveCompany}
              disabled={
                !vatNumberIsValid ||
                validationErrors.length > 0 ||
                saveInProgress
              }
            />{' '}
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
